import { useRef } from 'react';

import type { RowData, Table } from '@tanstack/react-table';
import type { DataTableColumnContent } from './types';

/** DataTable 컬럼 설정 UI에 사용할 컬럼 정보 메모제이션 */
export default function useDataTableColumns<TData extends RowData>(
  table: Table<TData>,
): DataTableColumnContent<TData>[] {
  const memoizedColumns = useRef<DataTableColumnContent<TData>[]>([]);
  const mColumnById = useRef<Record<string, DataTableColumnContent<TData>>>({});
  const comparingColumns: DataTableColumnContent<TData>[] = [];

  const columns = table.getAllFlatColumns();
  for (const column of columns) {
    const prev = mColumnById.current[column.id];
    const next: DataTableColumnContent<TData> = {
      id: column.id,
      headerName:
        (typeof column.columnDef.header === 'string' && column.columnDef.header) || column.id,
      visibleOnly: column.columnDef.enableHiding === false,
      isVisible: table.getState().columnVisibility?.[column.id] ?? true,
      defaultIsVisible: table.options.initialState?.columnVisibility?.[column.id] ?? true,
      disableHiding: column.columnDef.meta?.column?.showColumnVisibilityOption === false,
      orderIndex:
        table.getState().columnOrder.length > 0
          ? table.getState().columnOrder.indexOf(column.id)
          : -1,
      isColumnSizeChanged: !!table.getState().columnSizing[column.id],
    };

    const hasDiff =
      !prev ||
      prev.headerName !== next.headerName ||
      prev.visibleOnly !== next.visibleOnly ||
      prev.isVisible !== next.isVisible ||
      prev.defaultIsVisible !== next.defaultIsVisible ||
      prev.disableHiding !== next.disableHiding ||
      prev.orderIndex !== next.orderIndex ||
      prev.isColumnSizeChanged !== next.isColumnSizeChanged;

    if (hasDiff) {
      mColumnById.current[column.id] = next;
    }

    comparingColumns.push(mColumnById.current[column.id]);
  }

  if (!compareArrayShallow(memoizedColumns.current, comparingColumns)) {
    memoizedColumns.current = comparingColumns;
  }

  return [...memoizedColumns.current].sort((prev, next) => prev.orderIndex - next.orderIndex);
}

function compareArrayShallow<T>(source: T[], target: T[]): boolean {
  return source.length === target.length && source.every((value, index) => value === target[index]);
}
